import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "../assets/css/material-kit.css";

const TermsOfService = () => {
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');

  const changeLanguage = (lang: string) => {
    setCurrentLang(lang);
    i18n.changeLanguage(lang);
  };

  return (
    <div className="container my-5">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          {/* Language Disclaimer */}
          <div className="alert alert-info mb-4">
            <p className="mb-2">
              <strong>
                {currentLang === 'en' && 'This document is available in English, Georgian, and Russian. In case of any discrepancy, the English version shall prevail.'}
                {currentLang === 'ka' && 'ეს დოკუმენტი ხელმისაწვდომია ინგლისურად, ქართულად და რუსულად. გაურკვევლობის შემთხვევაში, უპირატესობა მიენიჭება ინგლისურ ვერსიას.'}
                {currentLang === 'ru' && 'Этот документ доступен на английском, грузинском и русском языках. В случае расхождений приоритет имеет английская версия.'}
              </strong>
            </p>
            <div className="btn-group btn-group-sm" role="group">
              <button 
                type="button" 
                className={`btn ${currentLang === 'en' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => changeLanguage('en')}
              >
                English
              </button>
              <button 
                type="button" 
                className={`btn ${currentLang === 'ka' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => changeLanguage('ka')}
              >
                ქართული
              </button>
              <button 
                type="button" 
                className={`btn ${currentLang === 'ru' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => changeLanguage('ru')}
              >
                Русский
              </button>
            </div>
          </div>

          <h1 className="mb-4">
            {currentLang === 'en' && 'Terms of Service'}
            {currentLang === 'ka' && 'მომსახურების პირობები'}
            {currentLang === 'ru' && 'Условия использования'}
          </h1>
          <p className="text-muted">
            <strong>
              {currentLang === 'en' && 'Last Updated:'}
              {currentLang === 'ka' && 'ბოლო განახლება:'}
              {currentLang === 'ru' && 'Последнее обновление:'}
            </strong> January 1, 2025
          </p>

          {currentLang === 'en' && (
            <>
              <section className="mb-4">
                <h2>1. Acceptance of Terms</h2>
                <p>
                  By accessing and using SyncOrb-AI's services, you agree to be bound by these Terms of Service. 
                  If you do not agree to these terms, please do not use our services.
                </p>
              </section>

              <section className="mb-4">
                <h2>2. Description of Service</h2>
                <p>
                  SyncOrb-AI provides AI-powered solutions including but not limited to machine learning, 
                  natural language processing, computer vision, and AI-powered applications. We reserve the 
                  right to modify, suspend, or discontinue any part of our services at any time.
                </p>
              </section>

              <section className="mb-4">
                <h2>3. User Responsibilities</h2>
                <p>You agree to:</p>
                <ul>
                  <li>Provide accurate and complete information when using our services</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Use our services in compliance with all applicable laws and regulations</li>
                  <li>Not misuse or attempt to gain unauthorized access to our systems</li>
                  <li>Not use our services for any illegal or unauthorized purpose</li>
                </ul>
              </section>

              <section className="mb-4">
                <h2>4. Intellectual Property</h2>
                <p>
                  All content, features, and functionality of SyncOrb-AI services are owned by SyncOrb-AI 
                  and are protected by international copyright, trademark, and other intellectual property laws. 
                  You may not reproduce, distribute, or create derivative works without our explicit permission.
                </p>
              </section>

              <section className="mb-4">
                <h2>5. Data Usage and Privacy</h2>
                <p>
                  Your use of our services is also governed by our Privacy Policy. We collect and process 
                  data as described in our Privacy Policy to provide and improve our services.
                </p>
              </section>

              <section className="mb-4">
                <h2>6. Limitation of Liability</h2>
                <p>
                  SyncOrb-AI shall not be liable for any indirect, incidental, special, consequential, or 
                  punitive damages resulting from your use or inability to use our services. Our total 
                  liability shall not exceed the amount paid by you for our services in the past 12 months.
                </p>
              </section>

              <section className="mb-4">
                <h2>7. Service Availability</h2>
                <p>
                  While we strive to maintain service availability, we do not guarantee uninterrupted access. 
                  We may suspend services for maintenance, updates, or due to circumstances beyond our control.
                </p>
              </section>

              <section className="mb-4">
                <h2>8. Termination</h2>
                <p>
                  We reserve the right to terminate or suspend your access to our services at our discretion, 
                  without notice, for conduct that violates these Terms of Service or is harmful to other users, 
                  us, or third parties.
                </p>
              </section>

              <section className="mb-4">
                <h2>9. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms of Service at any time. Changes will be effective 
                  immediately upon posting. Your continued use of our services constitutes acceptance of the 
                  modified terms.
                </p>
              </section>

              <section className="mb-4">
                <h2>10. Governing Law</h2>
                <p>
                  These Terms of Service shall be governed by and construed in accordance with the laws of 
                  Georgia, without regard to its conflict of law provisions.
                </p>
              </section>

              <section className="mb-4">
                <h2>11. Contact Information</h2>
                <p>
                  If you have any questions about these Terms of Service, please contact us through our 
                  Contact page or email us at support@syncorbai.com.
                </p>
              </section>
            </>
          )}

          {currentLang === 'ka' && (
            <>
              <section className="mb-4">
                <h2>1. პირობების მიღება</h2>
                <p>
                  SyncOrb-AI-ის სერვისების გამოყენებით, თქვენ ეთანხმებით ამ მომსახურების პირობებს. 
                  თუ თქვენ არ ეთანხმებით ამ პირობებს, გთხოვთ არ გამოიყენოთ ჩვენი სერვისები.
                </p>
              </section>

              <section className="mb-4">
                <h2>2. სერვისის აღწერა</h2>
                <p>
                  SyncOrb-AI უზრუნველყოფს AI-ით მართულ გადაწყვეტილებებს, მათ შორის მანქანურ სწავლებას, 
                  ბუნებრივი ენის დამუშავებას, კომპიუტერულ ხედვას და AI-ით მართულ აპლიკაციებს. ჩვენ 
                  ვიტოვებთ უფლებას შევცვალოთ, შევაჩეროთ ან შევწყვიტოთ ჩვენი სერვისების ნებისმიერი ნაწილი ნებისმიერ დროს.
                </p>
              </section>

              <section className="mb-4">
                <h2>3. მომხმარებლის პასუხისმგებლობები</h2>
                <p>თქვენ ეთანხმებით:</p>
                <ul>
                  <li>მიაწოდოთ ზუსტი და სრული ინფორმაცია ჩვენი სერვისების გამოყენებისას</li>
                  <li>შეინარჩუნოთ თქვენი ანგარიშის უსაფრთხოება</li>
                  <li>გამოიყენოთ ჩვენი სერვისები ყველა გამოყენებადი კანონისა და რეგულაციის შესაბამისად</li>
                  <li>არ გაუსწოროთ ან არ შეეცადოთ უნებართვო წვდომას ჩვენს სისტემებზე</li>
                  <li>არ გამოიყენოთ ჩვენი სერვისები უკანონო ან უნებართვო მიზნებისთვის</li>
                </ul>
              </section>

              <section className="mb-4">
                <h2>4. ინტელექტუალური საკუთრება</h2>
                <p>
                  SyncOrb-AI-ის სერვისების ყველა კონტენტი, ფუნქცია და ფუნქციონალი ეკუთვნის SyncOrb-AI-ს 
                  და დაცულია საერთაშორისო საავტორო უფლებებით, სავაჭრო ნიშნებითა და სხვა ინტელექტუალური 
                  საკუთრების კანონებით. თქვენ არ შეგიძლიათ გაამრავლოთ, გაავრცელოთ ან შექმნათ დამსახური ნამუშევრები ჩვენი ნათელი ნებართვის გარეშე.
                </p>
              </section>

              <section className="mb-4">
                <h2>5. მონაცემთა გამოყენება და კონფიდენციალურობა</h2>
                <p>
                  თქვენი სერვისების გამოყენება ასევე რეგულირდება ჩვენი კონფიდენციალურობის პოლიტიკით. 
                  ჩვენ ვაგროვებთ და ვამუშავებთ მონაცემებს, როგორც აღწერილია ჩვენს კონფიდენციალურობის პოლიტიკაში.
                </p>
              </section>

              <section className="mb-4">
                <h2>6. პასუხისმგებლობის შეზღუდვა</h2>
                <p>
                  SyncOrb-AI არ აგებს პასუხს ირიბი, შემთხვევითი, სპეციალური, შედეგობრივი ან დასჯადობრივი 
                  ზიანისთვის, რომელიც გამოწვეულია ჩვენი სერვისების გამოყენებით ან გამოყენების შეუძლებლობით. 
                  ჩვენი მთლიანი პასუხისმგებლობა არ უნდა აღემატებოდეს თანხას, რომელიც გადაიხადეთ ჩვენი სერვისებისთვის ბოლო 12 თვის განმავლობაში.
                </p>
              </section>

              <section className="mb-4">
                <h2>7. სერვისის ხელმისაწვდომობა</h2>
                <p>
                  მიუხედავად იმისა, რომ ჩვენ ვცდილობთ შევინარჩუნოთ სერვისის ხელმისაწვდომობა, ჩვენ არ ვიძლევით 
                  გარანტიას უწყვეტ წვდომაზე. ჩვენ შეგვიძლია შევაჩეროთ სერვისები მოვლა-შენახვისთვის, განახლებებისთვის 
                  ან ჩვენგან დამოუკიდებელი გარემოებების გამო.
                </p>
              </section>

              <section className="mb-4">
                <h2>8. შეწყვეტა</h2>
                <p>
                  ჩვენ ვიტოვებთ უფლებას შევაჩეროთ ან შევწყვიტოთ თქვენი წვდომა ჩვენს სერვისებზე ჩვენი განსჯით, 
                  გაფრთხილების გარეშე, თუ თქვენი ქცევა არღვევს ამ მომსახურების პირობებს ან არის საზიანო სხვა 
                  მომხმარებლებისთვის, ჩვენთვის ან მესამე მხარეებისთვის.
                </p>
              </section>

              <section className="mb-4">
                <h2>9. პირობების ცვლილებები</h2>
                <p>
                  ჩვენ ვიტოვებთ უფლებას შევცვალოთ ეს მომსახურების პირობები ნებისმიერ დროს. ცვლილებები 
                  ძალაში შევა დაუყოვნებლივ გამოქვეყნების შემდეგ. თქვენი ჩვენი სერვისების გამოყენების გაგრძელება 
                  ნიშნავს შეცვლილი პირობების მიღებას.
                </p>
              </section>

              <section className="mb-4">
                <h2>10. მოქმედი კანონმდებლობა</h2>
                <p>
                  ეს მომსახურების პირობები რეგულირდება და განიმარტება საქართველოს კანონების შესაბამისად, 
                  კანონების კონფლიქტის დებულებების გათვალისწინების გარეშე.
                </p>
              </section>

              <section className="mb-4">
                <h2>11. საკონტაქტო ინფორმაცია</h2>
                <p>
                  თუ თქვენ გაქვთ რაიმე კითხვა ამ მომსახურების პირობების შესახებ, გთხოვთ დაგვიკავშირდეთ ჩვენი 
                  კონტაქტის გვერდის მეშვეობით ან მოგვწერეთ support@syncorbai.com.
                </p>
              </section>
            </>
          )}

          {currentLang === 'ru' && (
            <>
              <section className="mb-4">
                <h2>1. Принятие условий</h2>
                <p>
                  Получая доступ и используя услуги SyncOrb-AI, вы соглашаетесь соблюдать настоящие Условия использования. 
                  Если вы не согласны с этими условиями, пожалуйста, не используйте наши услуги.
                </p>
              </section>

              <section className="mb-4">
                <h2>2. Описание услуги</h2>
                <p>
                  SyncOrb-AI предоставляет решения на основе ИИ, включая, но не ограничиваясь, машинное обучение, 
                  обработку естественного языка, компьютерное зрение и приложения на базе ИИ. Мы оставляем за собой 
                  право изменять, приостанавливать или прекращать любую часть наших услуг в любое время.
                </p>
              </section>

              <section className="mb-4">
                <h2>3. Обязанности пользователя</h2>
                <p>Вы соглашаетесь:</p>
                <ul>
                  <li>Предоставлять точную и полную информацию при использовании наших услуг</li>
                  <li>Поддерживать безопасность учетных данных вашей учетной записи</li>
                  <li>Использовать наши услуги в соответствии со всеми применимыми законами и правилами</li>
                  <li>Не злоупотреблять и не пытаться получить несанкционированный доступ к нашим системам</li>
                  <li>Не использовать наши услуги в незаконных или несанкционированных целях</li>
                </ul>
              </section>

              <section className="mb-4">
                <h2>4. Интеллектуальная собственность</h2>
                <p>
                  Весь контент, функции и функциональность услуг SyncOrb-AI принадлежат SyncOrb-AI и защищены 
                  международными законами об авторском праве, товарных знаках и другой интеллектуальной собственности. 
                  Вы не можете воспроизводить, распространять или создавать производные работы без нашего явного разрешения.
                </p>
              </section>

              <section className="mb-4">
                <h2>5. Использование данных и конфиденциальность</h2>
                <p>
                  Использование наших услуг также регулируется нашей Политикой конфиденциальности. Мы собираем и 
                  обрабатываем данные, как описано в нашей Политике конфиденциальности, для предоставления и улучшения наших услуг.
                </p>
              </section>

              <section className="mb-4">
                <h2>6. Ограничение ответственности</h2>
                <p>
                  SyncOrb-AI не несет ответственности за любые косвенные, случайные, особые, последующие или штрафные 
                  убытки, возникшие в результате использования или невозможности использования наших услуг. Наша общая 
                  ответственность не превышает сумму, уплаченную вами за наши услуги за последние 12 месяцев.
                </p>
              </section>

              <section className="mb-4">
                <h2>7. Доступность услуги</h2>
                <p>
                  Хотя мы стремимся поддерживать доступность услуг, мы не гарантируем непрерывный доступ. Мы можем 
                  приостановить услуги для обслуживания, обновлений или по обстоятельствам, не зависящим от нас.
                </p>
              </section>

              <section className="mb-4">
                <h2>8. Прекращение</h2>
                <p>
                  Мы оставляем за собой право прекратить или приостановить ваш доступ к нашим услугам по нашему усмотрению, 
                  без уведомления, за поведение, нарушающее настоящие Условия использования или причиняющее вред другим 
                  пользователям, нам или третьим лицам.
                </p>
              </section>

              <section className="mb-4">
                <h2>9. Изменения условий</h2>
                <p>
                  Мы оставляем за собой право изменять настоящие Условия использования в любое время. Изменения вступают 
                  в силу немедленно после публикации. Ваше дальнейшее использование наших услуг означает принятие 
                  измененных условий.
                </p>
              </section>

              <section className="mb-4">
                <h2>10. Применимое право</h2>
                <p>
                  Настоящие Условия использования регулируются и толкуются в соответствии с законами Грузии, без учета 
                  положений о конфликте законов.
                </p>
              </section>

              <section className="mb-4">
                <h2>11. Контактная информация</h2>
                <p>
                  Если у вас есть вопросы относительно настоящих Условий использования, пожалуйста, свяжитесь с нами 
                  через нашу страницу контактов или напишите нам на support@syncorbai.com.
                </p>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
